import React, { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api';
import { useSocket } from './SocketContext';
import { getPendingReferralCode, clearReferralCode } from '../utils/referralCapture';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (identifier: string, password: string) => Promise<void>;
  loginWithOTP: (email: string, otp: string) => Promise<void>;
  sendOTP: (email: string) => Promise<void>;
  signup: (userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<any>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<any>;
  clearError: () => void;
  refreshUser: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Debug: Log state changes (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthContext State Changed:', {
        isAuthenticated: state.isAuthenticated,
        user: state.user?.id,
        isLoading: state.isLoading,
        error: state.error
      });
    }
  }, [state.isAuthenticated, state.user?.id, state.isLoading, state.error]);

  // Helper function to check cookies (only in development)
  const checkCookies = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthContext: Checking cookies...');
      const cookies = document.cookie;
      const tokenCookie = cookies.split(';').find(cookie => cookie.trim().startsWith('token='));
      if (tokenCookie) {
        console.log('AuthContext: Token cookie found');
      } else {
        console.log('AuthContext: No token cookie found');
      }
    }
  };

  // Check if user is authenticated on app load (only once)
  useEffect(() => {
    if (!hasCheckedAuth) {
      setHasCheckedAuth(true);
      const checkAuth = async () => {
        try {
          console.log('AuthContext: Checking authentication...');
          dispatch({ type: 'AUTH_START' });
          const response = await apiService.getCurrentUser();
          console.log('AuthContext /api/auth/me response:', response);
          
          if (response.success && response.data) {
            console.log('AuthContext: Setting user from response.data:', response.data);
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user: response.data, token: '' },
            });
          } else {
            console.log('AuthContext: Authentication failed - no user data');
            dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication failed' });
          }
        } catch (error) {
          console.error('AuthContext error:', error);
          dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication failed' });
        }
      };
      checkAuth();
    }
  }, [hasCheckedAuth]);

  // Dispatch auth state changes for socket provider
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('authStateChange', {
      detail: {
        isAuthenticated: state.isAuthenticated,
        user: state.user
      }
    }));
  }, [state.isAuthenticated, state.user]);

  // Centralized referral-applying trigger when authenticated
  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      const code = getPendingReferralCode();
      if (code) {
        const applyReferral = async () => {
          try {
            console.log('[Referral] Applying pending referral code:', code);
            await apiService.recordSignup({
              referredUserId: state.user!._id,
              referralCode: code
            });
            console.log('[Referral] Referral recorded successfully.');
          } catch (err: any) {
            console.error('[Referral] Failed to record referral:', err.message);
          } finally {
            clearReferralCode();
          }
        };
        applyReferral();
      }
    }
  }, [state.isAuthenticated, state.user]);

  const login = async (identifier: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await apiService.login({ identifier, password });
      
      if (response.success) {
        // After login, fetch user from /auth/me using cookie
        const me = await apiService.getCurrentUser();
        
        if (me.success && me.data) {
          checkCookies(); // Check if token cookie was set
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user: me.data, token: '' },
          });
        } else {
          dispatch({ type: 'AUTH_FAILURE', payload: 'Login failed: could not fetch user' });
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.message || 'Login failed' });
      }
    } catch (error: any) {
      console.error('AuthContext error:', error);
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.response?.data?.message || error.message || 'Authentication failed',
      });
    }
  };

  const loginWithOTP = async (email: string, otp: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await apiService.verifyOTP(email, otp);
      
      if (response.success) {
        // After OTP verification, fetch user from /auth/me using cookie
        const me = await apiService.getCurrentUser();
        
        if (me.success && me.data) {
          checkCookies(); // Check if token cookie was set
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user: me.data, token: '' },
          });
        } else {
          dispatch({ type: 'AUTH_FAILURE', payload: 'OTP login failed: could not fetch user' });
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.message || 'OTP verification failed' });
      }
    } catch (error: any) {
      console.error('AuthContext OTP error:', error);
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.response?.data?.message || error.message || 'OTP authentication failed',
      });
    }
  };

  const sendOTP = async (email: string) => {
    try {
      const response = await apiService.loginWithOTP(email);
      if (!response.success) {
        throw new Error(response.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      console.error('Send OTP error:', error);
      throw error;
    }
  };

  const signup = async (userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await apiService.signup(userData);
      
      if (response.success) {
        // Don't automatically log in after signup - user needs to verify email
        dispatch({ type: 'CLEAR_ERROR' });
        return response; // Return the response for the signup page to handle
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.message || 'Signup failed' });
        throw new Error(response.message || 'Signup failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Signup failed';
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      console.log('AuthContext: updateUser called with:', userData);
      console.log('AuthContext: Current state before update:', {
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        isLoading: state.isLoading
      });
      
      const response = await apiService.updateProfile(userData);
      console.log('AuthContext: updateProfile response:', response);
      
      if (response.success && response.data) {
        console.log('AuthContext: Dispatching UPDATE_USER with:', response.data);
        
        // Ensure the updated user has the same ID as the current user
        if (state.user && response.data._id !== state.user._id) {
          console.error('AuthContext: User ID mismatch after update!');
          throw new Error('User ID mismatch after profile update');
        }
        
        dispatch({ type: 'UPDATE_USER', payload: response.data });
        console.log('AuthContext: UPDATE_USER dispatched successfully');
        
        // Verify the state was updated correctly
        console.log('AuthContext: State should be updated now');
        
        // Return the response for the calling function
        return response;
      } else {
        console.log('AuthContext: Profile update failed:', response.message);
        dispatch({
          type: 'AUTH_FAILURE',
          payload: response.message || 'Profile update failed',
        });
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error: any) {
      console.error('AuthContext: updateUser error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Profile update failed';
      
      // Don't set AUTH_FAILURE for profile update errors as it makes the user appear logged out
      // Just throw the error to be handled by the calling component
      throw error;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async () => {
    try {
      console.log('AuthContext: Refreshing user data...');
      const response = await apiService.getCurrentUser();
      console.log('AuthContext: Refresh response:', response);
      
      if (response.success && response.data) {
        console.log('AuthContext: User data refreshed successfully:', response.data);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: response.data, token: '' },
        });
        return true;
      } else {
        console.log('AuthContext: Failed to refresh user data');
        dispatch({ type: 'AUTH_FAILURE', payload: 'Failed to refresh user data' });
        return false;
      }
    } catch (error) {
      console.error('AuthContext: Refresh user error:', error);
      dispatch({ type: 'AUTH_FAILURE', payload: 'Failed to refresh user data' });
      return false;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithOTP,
    sendOTP,
    signup,
    logout,
    updateUser,
    clearError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 